import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Eye, EyeOff, Upload, X, 
  Loader2, Link as LinkIcon, Image as ImageIcon, Check, AlertCircle,
  Home, Building2, DollarSign, MapPin, Bed, Bath, Square
} from 'lucide-react';
import { notifyError } from '../../lib/notify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Budget range options
const RENT_RANGES = [
  { value: 'under_2000', label: 'Under $2,000' },
  { value: '2000_5000', label: '$2,000 - $5,000' },
  { value: '5000_10000', label: '$5,000 - $10,000' },
  { value: 'over_10000', label: 'Over $10,000' }
];

const BUY_RANGES = [
  { value: '250_500', label: '$250K - $500K' },
  { value: '500_1m', label: '$500K - $1M' },
  { value: '1m_3m', label: '$1M - $3M' },
  { value: '3m_10m', label: '$3M - $10M' },
  { value: 'over_10m', label: 'Over $10M' }
];

const AdminListings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);

  function getEmptyForm() {
    return {
      title: '',
      city: 'Mexico City',
      neighborhood: '',
      intent: 'rent',
      price_amount: '',
      price_currency: 'USD',
      rent_range: '',
      buy_range: '',
      bedrooms: '',
      bathrooms: '',
      area_sqm: '',
      description: '',
      tags: [],
      source_url: '',
      status: 'draft',
      images: []
    };
  }

  useEffect(() => {
    verifyAdmin();
    fetchListings();
  }, []);

  const verifyAdmin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, { credentials: 'include' });
      if (!response.ok) navigate('/admin');
    } catch {
      navigate('/admin');
    }
  };

  const fetchListings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/curated-listings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings || []);
      }
    } catch (err) {
      console.error('Error fetching listings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportUrl = async () => {
    if (!importUrl.trim()) return;
    
    setImporting(true);
    setImportError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/curated-listings/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ source_url: importUrl })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          title: data.title || prev.title,
          description: data.description || prev.description,
          city: data.city || prev.city,
          neighborhood: data.neighborhood || prev.neighborhood,
          price_amount: data.price_amount || prev.price_amount,
          price_currency: data.price_currency || prev.price_currency,
          bedrooms: data.bedrooms || prev.bedrooms,
          bathrooms: data.bathrooms || prev.bathrooms,
          area_sqm: data.area_sqm || prev.area_sqm,
          source_url: importUrl,
          // Note: images are URLs, would need separate download logic
        }));
        setImportUrl('');
      } else {
        setImportError(data.error || 'Unable to extract. Please enter manually.');
      }
    } catch (err) {
      setImportError('Unable to extract. Please enter manually.');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const payload = {
        ...formData,
        price_amount: parseFloat(formData.price_amount) || 0,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        area_sqm: formData.area_sqm ? parseFloat(formData.area_sqm) : null,
        tags: Array.isArray(formData.tags) ? formData.tags : formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      
      const url = editingListing 
        ? `${API_URL}/api/admin/curated-listings/${editingListing.id}`
        : `${API_URL}/api/admin/curated-listings`;
      
      const response = await fetch(url, {
        method: editingListing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save listing');
      }
      
      const savedListing = await response.json();
      
      setShowForm(false);
      setEditingListing(null);
      setFormData(getEmptyForm());
      fetchListings();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setFormData({
      ...listing,
      tags: listing.tags || [],
      price_amount: listing.price_amount || '',
      bedrooms: listing.bedrooms || '',
      bathrooms: listing.bathrooms || '',
      area_sqm: listing.area_sqm || ''
    });
    setShowForm(true);
  };

  const handlePublish = async (listing) => {
    try {
      const action = listing.status === 'published' ? 'unpublish' : 'publish';
      const response = await fetch(`${API_URL}/api/admin/curated-listings/${listing.id}/${action}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) fetchListings();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleDelete = async (listing) => {
    if (!window.confirm(`Delete "${listing.title}"? This cannot be undone.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/curated-listings/${listing.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) fetchListings();
    } catch (err) {
      console.error('Error deleting listing:', err);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !editingListing) return;
    
    setUploadingImages(true);
    
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      const response = await fetch(`${API_URL}/api/admin/curated-listings/${editingListing.id}/images/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, images: data.images }));
        setEditingListing(prev => ({ ...prev, images: data.images }));
      } else {
        const err = await response.json().catch(() => ({}));
        notifyError(err, 'Failed to upload images.');
      }
    } catch (err) {
      console.error('Upload error:', err);
      notifyError('Failed to upload images.');
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (filename) => {
    if (!editingListing) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/curated-listings/${editingListing.id}/images/${filename}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, images: data.images }));
        setEditingListing(prev => ({ ...prev, images: data.images }));
      }
    } catch (err) {
      console.error('Delete image error:', err);
    }
  };

  const formatPrice = (amount, currency) => {
    if (!amount) return '-';
    return `${currency === 'MXN' ? 'MX$' : '$'}${amount.toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago'});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      {/* Header */}
      <div className="border-b border-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#0A1F2E]/60 hover:text-[#0A1F2E] transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-medium text-white">Curated Listings</h1>
              <p className="text-sm text-[#666]">{listings.length} listings</p>
            </div>
          </div>
          
          <button
            onClick={() => {
              setEditingListing(null);
              setFormData(getEmptyForm());
              setShowForm(true);
            }}
            className="flex items-center gap-2 bg-[#2CA8A0] text-black px-4 py-2 text-sm font-medium hover:bg-[#2CA8A0] transition"
            data-testid="add-listing-btn"
          >
            <Plus className="w-4 h-4" />
            Add Listing
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Listings Table */}
        {!showForm && (
          <div className="bg-[#F9F6EF] border border-[#FFFFFF] overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#FFFFFF]">
                  <th className="text-left px-4 py-3 text-xs text-[#666] uppercase tracking-wider">Title</th>
                  <th className="text-left px-4 py-3 text-xs text-[#666] uppercase tracking-wider">City</th>
                  <th className="text-left px-4 py-3 text-xs text-[#666] uppercase tracking-wider">Intent</th>
                  <th className="text-left px-4 py-3 text-xs text-[#666] uppercase tracking-wider">Price</th>
                  <th className="text-left px-4 py-3 text-xs text-[#666] uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs text-[#666] uppercase tracking-wider">Updated</th>
                  <th className="text-right px-4 py-3 text-xs text-[#666] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-[#666]">
                      No listings yet. Click "Add Listing" to create one.
                    </td>
                  </tr>
                ) : (
                  listings.map((listing) => (
                    <tr key={listing.id} className="border-b border-[#FFFFFF] hover:bg-[#111]">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {listing.images?.[0] ? (
                            <img 
                              src={`${API_URL}${listing.images[0].url}`}
                              alt=""
                              className="w-12 h-12 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-[#FFFFFF] rounded flex items-center justify-center">
                              <ImageIcon className="w-5 h-5 text-[#444]" />
                            </div>
                          )}
                          <span className="text-[#0A1F2E] font-medium">{listing.title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#888]">{listing.city}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded ${
                          listing.intent === 'rent' 
                            ? 'bg-blue-500/20 text-blue-400' 
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {listing.intent === 'rent' ? 'Rent' : 'Buy'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white">{formatPrice(listing.price_amount, listing.price_currency)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded ${
                          listing.status === 'published' 
                            ? 'bg-[#2CA8A0]/20 text-[#2CA8A0]' 
                            : 'bg-[#333] text-[#888]'
                        }`}>
                          {listing.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#666] text-sm">{formatDate(listing.updated_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handlePublish(listing)}
                            className="p-2 text-[#666] hover:text-white transition"
                            title={listing.status === 'published' ? 'Unpublish' : 'Publish'}
                          >
                            {listing.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleEdit(listing)}
                            className="p-2 text-[#666] hover:text-white transition"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(listing)}
                            className="p-2 text-[#666] hover:text-red-400 transition"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-[#F9F6EF] border border-[#FFFFFF] p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-medium text-white">
                {editingListing ? 'Edit Listing' : 'Add New Listing'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingListing(null);
                  setFormData(getEmptyForm());
                }}
                className="text-[#666] hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Import by URL */}
            <div className="mb-8 p-6 bg-[#F9F6EF] border border-[#FFFFFF]">
              <h3 className="text-sm text-[#888] uppercase tracking-wider mb-4">Import by URL (Optional)</h3>
              <div className="flex gap-3">
                <div className="flex-1 flex items-center gap-2 bg-[#111] border border-[#222] px-3">
                  <LinkIcon className="w-4 h-4 text-[#666]" />
                  <input
                    type="url"
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    placeholder="Paste listing URL to prefill..."
                    className="flex-1 bg-transparent py-2.5 text-white placeholder-[#555] focus:outline-none"
                  />
                </div>
                <button
                  onClick={handleImportUrl}
                  disabled={importing || !importUrl.trim()}
                  className="px-4 py-2 bg-[#222] text-white text-sm hover:bg-[#333] transition disabled:opacity-50"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch Details'}
                </button>
              </div>
              {importError && (
                <p className="mt-2 text-sm text-[#ff8888] flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {importError}
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">
                    Title <span className="text-[#2CA8A0]">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                    data-testid="listing-title"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Neighborhood</label>
                  <input
                    type="text"
                    value={formData.neighborhood}
                    onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                    placeholder="e.g. Roma Norte, Polanco"
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white placeholder-[#555] focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>
              </div>

              {/* Intent & Price */}
              <div className="grid md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">
                    Intent <span className="text-[#2CA8A0]">*</span>
                  </label>
                  <select
                    value={formData.intent}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      intent: e.target.value,
                      rent_range: '',
                      buy_range: ''
                    })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  >
                    <option value="rent">Rent</option>
                    <option value="buy">Buy</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">
                    Price <span className="text-[#2CA8A0]">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.price_amount}
                    onChange={(e) => setFormData({ ...formData, price_amount: e.target.value })}
                    required
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Currency</label>
                  <select
                    value={formData.price_currency}
                    onChange={(e) => setFormData({ ...formData, price_currency: e.target.value })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  >
                    <option value="USD">USD</option>
                    <option value="MXN">MXN</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">
                    {formData.intent === 'rent' ? 'Rent Range' : 'Buy Range'}
                  </label>
                  <select
                    value={formData.intent === 'rent' ? formData.rent_range : formData.buy_range}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      [formData.intent === 'rent' ? 'rent_range' : 'buy_range']: e.target.value 
                    })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  >
                    <option value="">Select range...</option>
                    {(formData.intent === 'rent' ? RENT_RANGES : BUY_RANGES).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Property Details */}
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Bedrooms</label>
                  <input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    min="0"
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Bathrooms</label>
                  <input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    min="0"
                    step="0.5"
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Area (m²)</label>
                  <input
                    type="number"
                    value={formData.area_sqm}
                    onChange={(e) => setFormData({ ...formData, area_sqm: e.target.value })}
                    min="0"
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none resize-none"
                />
              </div>

              {/* Tags & Source */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={Array.isArray(formData.tags) ? formData.tags.join(', ') : formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="luxury, furnished, view"
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white placeholder-[#555] focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Source URL</label>
                  <input
                    type="url"
                    value={formData.source_url}
                    onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full md:w-48 bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>

              {/* Images (only for editing) */}
              {editingListing && (
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-4">
                    Images ({formData.images?.length || 0}/8)
                  </label>
                  
                  <div className="flex flex-wrap gap-4 mb-4">
                    {formData.images?.map((img, idx) => (
                      <div key={img.filename} className="relative group">
                        <img 
                          src={`${API_URL}${img.url}`}
                          alt=""
                          className="w-24 h-24 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(img.filename)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                    
                    {(formData.images?.length || 0) < 8 && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImages}
                        className="w-24 h-24 border border-dashed border-[#333] rounded flex flex-col items-center justify-center gap-1 text-[#666] hover:text-white hover:border-[#555] transition"
                      >
                        {uploadingImages ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            <span className="text-xs">Upload</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <p className="text-xs text-[#666]">JPEG, PNG, WebP. Max 5MB each.</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-4 pt-6 border-t border-[#FFFFFF]">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-[#2CA8A0] text-black px-6 py-3 font-medium hover:bg-[#2CA8A0] transition disabled:opacity-50"
                  data-testid="save-listing-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingListing ? 'Save Changes' : 'Create Listing'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingListing(null);
                    setFormData(getEmptyForm());
                  }}
                  className="px-6 py-3 text-[#0A1F2E]/60 hover:text-[#0A1F2E] transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminListings;
